import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import {
  hasProductActivatedStatus,
  isProductRegistered,
} from '@utils/AccountAccess/accountProductStatuses'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { TickmillProductType } from '@model/TickmillProductType'

export const useTryProduct = (tickmillProductType: TickmillProductType) => {
  const { account } = useAccountReadContext()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { refreshAccount } = useAccountWriteContext()
  const locale = useSessionLanguage()

  const isRegistered = useMemo(
    () => isProductRegistered(account, tickmillProductType),
    [account, tickmillProductType]
  )

  const isActivated = useMemo(
    () => hasProductActivatedStatus(account, tickmillProductType),
    [account, tickmillProductType]
  )

  const addProduct = useCallback(async () => {
    if (!isRegistered) {
      await apiClient.addTickmillProduct(tickmillProductType)
      await queryClient.invalidateQueries({ queryKey: ['profile'] })
      await refreshAccount(locale)
    }

    return
  }, [apiClient, isRegistered, locale, queryClient, refreshAccount, tickmillProductType])

  return {
    addProduct,
    isRegistered,
    isActivated,
  }
}
